import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

function HoursReport() {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ col: 'name', dir: 'asc' });
  const [filters, setFilters] = useState({ name: '', department: '', status: '' });
  const [combined, setCombined] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/timeslots/admin/data`);
      setDepartments(res.data.departments);
      const deptMap = Object.fromEntries(res.data.departments.map(d => [d.id, d.name]));
      const slotMap = Object.fromEntries(res.data.timeslots.map(s => [s.id, s]));

      const flat = res.data.signups.map(signup => {
        const slot = slotMap[signup.timeslot_id] || {};
        return {
          id: signup.id,
          volunteer_id: signup.volunteer_id,
          name: signup.name,
          phone: signup.phone,
          department: deptMap[slot.department_id] || 'Unknown',
          slot_start: slot.start_time,
          slot_end: slot.end_time,
          actual_start: signup.actual_start_time || '',
          actual_end: signup.actual_end_time || '',
          status: signup.status,
          approved_by: signup.approved_by || '',
          double_time: !!slot.double_time,
        };
      });
      setRows(flat);
    } catch (err) {
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const computeHours = (row) => {
    if (row.status !== 'approved') return null;
    const start = row.actual_start || row.slot_start;
    const end = row.actual_end || row.slot_end;
    if (!start || !end) return null;
    const raw = (new Date(end) - new Date(start)) / 3600000;
    const multiplied = row.double_time ? raw * 2 : raw;
    return Math.round(multiplied * 10) / 10;
  };

  const fmtTime = (str) => {
    if (!str) return '—';
    return new Date(str).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const fmtShift = (start, end) => {
    if (!start || !end) return '—';
    const s = new Date(start);
    const e = new Date(end);
    const date = s.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const t1 = s.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const t2 = e.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date} • ${t1} – ${t2}`;
  };

  const filtered = rows.filter(r =>
    (!filters.name || r.name.toLowerCase().includes(filters.name.toLowerCase())) &&
    (!filters.department || r.department === filters.department) &&
    (!filters.status || r.status === filters.status)
  );

  const applySortTo = (items) => [...items].sort((a, b) => {
    let av = a[sortConfig.col] ?? '';
    let bv = b[sortConfig.col] ?? '';
    if (typeof av === 'string' && typeof bv === 'string') {
      av = av.toLowerCase();
      bv = bv.toLowerCase();
    } else {
      av = av === '' ? -Infinity : av;
      bv = bv === '' ? -Infinity : bv;
    }
    if (av < bv) return sortConfig.dir === 'asc' ? -1 : 1;
    if (av > bv) return sortConfig.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const sorted = applySortTo(filtered);

  const combinedRows = (() => {
    const map = {};
    // Fallback key (when a signup has no linked volunteer_id) needs to be
    // case-insensitive on the name and digits-only on the phone so that
    // "Jane Doe" / "jane doe" / "JANE DOE" with the same phone — even with
    // different punctuation — collapse into one row instead of three.
    const nameKey = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const phoneKey = (s) => (s || '').replace(/\D/g, '');
    filtered.forEach(r => {
      const key = r.volunteer_id != null
        ? `v${r.volunteer_id}`
        : `${nameKey(r.name)}||${phoneKey(r.phone)}`;
      if (!map[key]) {
        map[key] = {
          name: r.name,
          phone: r.phone,
          departments: new Set(),
          shifts: 0,
          scheduledHours: 0,
          actualHours: 0,
          statuses: new Set(),
        };
      }
      const e = map[key];
      const mult = r.double_time ? 2 : 1;
      e.departments.add(r.department);
      e.shifts++;
      if (r.slot_start && r.slot_end)
        e.scheduledHours += ((new Date(r.slot_end) - new Date(r.slot_start)) / 3600000) * mult;
      if (r.status === 'approved') {
        const start = r.actual_start || r.slot_start;
        const end = r.actual_end || r.slot_end;
        if (start && end)
          e.actualHours += ((new Date(end) - new Date(start)) / 3600000) * mult;
      }
      e.statuses.add(r.status);
    });
    return Object.values(map).map(e => ({
      name: e.name,
      phone: e.phone,
      departments: [...e.departments].join(', '),
      shifts: e.shifts,
      scheduledHours: Math.round(e.scheduledHours * 10) / 10,
      actualHours: e.actualHours > 0 ? Math.round(e.actualHours * 10) / 10 : null,
      statuses: [...e.statuses].join(', '),
    }));
  })();

  const sortedCombined = applySortTo(combinedRows);

  const toggleSort = (col) =>
    setSortConfig(prev => ({
      col,
      dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc'
    }));

  const ind = (col) => sortConfig.col === col ? (sortConfig.dir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';

  const exportCSV = () => {
    let headers, dataRows;
    if (combined) {
      headers = ['Name', 'Phone', 'Departments', 'Shifts', 'Scheduled Hours', 'Actual Hours', 'Statuses'];
      dataRows = sortedCombined.map(r => [
        r.name, r.phone, r.departments, r.shifts,
        r.scheduledHours, r.actualHours ?? '', r.statuses
      ]);
    } else {
      headers = ['Name', 'Phone', 'Department', 'Scheduled Shift', 'Actual Start', 'Actual End', 'Hours Worked', 'Status', 'Approved By'];
      dataRows = sorted.map(r => [
        r.name, r.phone, r.department,
        fmtShift(r.slot_start, r.slot_end),
        fmtTime(r.actual_start),
        fmtTime(r.actual_end),
        computeHours(r) ?? '',
        r.status,
        r.approved_by
      ]);
    }
    const esc = (v) => {
      let s = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [headers, ...dataRows].map(row => row.map(esc).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteer-hours-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="report-loading">Loading report data...</div>;

  return (
    <div className="hours-report">

      <div className="report-controls">
        <div className="report-filters">
          <input
            className="report-filter-input"
            type="text"
            placeholder="Filter by name..."
            value={filters.name}
            onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
          />
          <select
            className="report-filter-select"
            value={filters.department}
            onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}
          >
            <option value="">All Departments</option>
            {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(d => (
              <option key={d.id} value={d.name}>{d.name}</option>
            ))}
          </select>
          <select
            className="report-filter-select"
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="report-actions">
          <button
            className={`report-toggle-btn ${combined ? 'active' : ''}`}
            onClick={() => { setCombined(c => !c); setSortConfig({ col: 'name', dir: 'asc' }); }}
          >
            {combined ? 'EXPAND' : 'COMBINE BY PERSON'}
          </button>
          <button className="report-export-btn" onClick={exportCSV}>
            EXPORT CSV
          </button>
        </div>
      </div>

      <div className="report-meta">
        {combined
          ? `${sortedCombined.length} volunteer${sortedCombined.length !== 1 ? 's' : ''}`
          : `${sorted.length} record${sorted.length !== 1 ? 's' : ''}`}
      </div>

      {combined ? (
        <div className="report-table-wrap">
          {sortedCombined.length === 0 ? (
            <div className="no-data">No records match the current filters.</div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  {[
                    ['name', 'Name'],
                    ['phone', 'Phone'],
                    ['departments', 'Departments'],
                    ['shifts', 'Shifts'],
                    ['scheduledHours', 'Sched. Hrs'],
                    ['actualHours', 'Actual Hrs'],
                    ['statuses', 'Status'],
                  ].map(([col, label]) => (
                    <th key={col} onClick={() => toggleSort(col)} className={sortConfig.col === col ? 'sorted' : ''}>
                      {label}{ind(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedCombined.map((r, i) => (
                  <tr key={`${r.name}-${r.phone}`} className={i % 2 === 0 ? 'even' : 'odd'}>
                    <td className="report-name">{r.name}</td>
                    <td className="phone-cell">{r.phone}</td>
                    <td>{r.departments}</td>
                    <td className="report-num">{r.shifts}</td>
                    <td className="report-num">{r.scheduledHours}h</td>
                    <td className="report-num">{r.actualHours != null ? `${r.actualHours}h` : '—'}</td>
                    <td><StatusBadges statuses={r.statuses} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="report-table-wrap">
          {sorted.length === 0 ? (
            <div className="no-data">No records match the current filters.</div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  {[
                    ['name', 'Name'],
                    ['phone', 'Phone'],
                    ['department', 'Department'],
                    ['slot_start', 'Scheduled Shift'],
                    ['actual_start', 'Actual Start'],
                    ['actual_end', 'Actual End'],
                    [null, 'Hrs'],
                    ['status', 'Status'],
                    ['approved_by', 'Approved By'],
                  ].map(([col, label]) => (
                    <th
                      key={label}
                      onClick={col ? () => toggleSort(col) : undefined}
                      className={[col ? 'sortable' : '', sortConfig.col === col ? 'sorted' : ''].join(' ').trim()}
                    >
                      {label}{col ? ind(col) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const hrs = computeHours(r);
                  const hasActual = !!r.actual_start;
                  return (
                    <tr key={`${r.id}`} className={i % 2 === 0 ? 'even' : 'odd'}>
                      <td className="report-name">{r.name}</td>
                      <td className="phone-cell">{r.phone}</td>
                      <td>{r.department}</td>
                      <td>{fmtShift(r.slot_start, r.slot_end)}</td>
                      <td className={hasActual ? 'report-actual' : 'report-dash'}>{fmtTime(r.actual_start)}</td>
                      <td className={hasActual ? 'report-actual' : 'report-dash'}>{fmtTime(r.actual_end)}</td>
                      <td className="report-num">{hrs != null ? `${hrs}h` : '—'}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="report-muted">{r.approved_by || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`status-badge status-${status}`}>{status}</span>;
}

function StatusBadges({ statuses }) {
  return (
    <span className="status-badge-group">
      {statuses.split(', ').map(s => <StatusBadge key={s} status={s} />)}
    </span>
  );
}

export default HoursReport;
